/**
 * カードの種類・属性・種族（ygopro の type / attribute / race のビット）と、カードの検索（`SearchCards`）に送る値。
 * 属性・種族の表は Godot の `ui/look_panel.gd` と同じ。種類は儀式・融合・シンクロ、魔法・罠は細分（速攻・永続・カウンター…）まで
 * type のビットで絞る（`type_any` / `type_none`）。制限・準制限は lflist から番号の一覧（`ids`）にして送る（Issue #868 / #954 / #957）。
 */

export const TYPE_MONSTER = 0x1;
export const TYPE_SPELL = 0x2;
export const TYPE_TRAP = 0x4;
const EXTRA_MASK = 0x40 | 0x2000 | 0x800000 | 0x4000000;   // 融合・シンクロ・エクシーズ・リンク

// [値, 表示, SearchCards の param]
export const KINDS = [
  ['', 'すべての種類', {}],
  ['monster', 'モンスター（メイン）', { kind: 'monster' }],
  ['ritual', '儀式モンスター', { kind: 'monster', type_any: 0x80 }],
  ['fusion', '融合モンスター', { kind: 'extra', type_any: 0x40 }],
  ['synchro', 'シンクロモンスター', { kind: 'extra', type_any: 0x2000 }],
  ['extra', 'エクストラ（すべて）', { kind: 'extra' }],
  ['spell', '魔法', { kind: 'spell' }],
  ['trap', '罠', { kind: 'trap' }],
];
const SPELL_SUB_BITS = 0x10000 | 0x20000 | 0x40000 | 0x80000 | 0x80;
const TRAP_SUB_BITS = 0x20000 | 0x100000;
/** 魔法・罠の細分（種類が魔法・罠のときだけ） */
export const SUBKINDS = {
  spell: [['', 'すべての魔法', {}], ['normal', '通常魔法', { type_none: SPELL_SUB_BITS }], ['quick', '速攻魔法', { type_any: 0x10000 }],
    ['continuous', '永続魔法', { type_any: 0x20000 }], ['equip', '装備魔法', { type_any: 0x40000 }],
    ['field', 'フィールド魔法', { type_any: 0x80000 }], ['ritual', '儀式魔法', { type_any: 0x80 }]],
  trap: [['', 'すべての罠', {}], ['normal', '通常罠', { type_none: TRAP_SUB_BITS }], ['continuous', '永続罠', { type_any: 0x20000 }],
    ['counter', 'カウンター罠', { type_any: 0x100000 }]],
};
/** 禁止・制限（lflist の枚数）: [値, 表示, 許す枚数] */
export const LIMITS = [['', '制限なしも含む', null], ['0', '禁止', [0]], ['1', '制限', [1]], ['2', '準制限', [2]], ['12', '制限・準制限', [1, 2]]];
export const ATTRIBUTES = [[0x1, '地'], [0x2, '水'], [0x4, '炎'], [0x8, '風'], [0x10, '光'], [0x20, '闇'], [0x40, '神']];
export const RACES = [[0x1, '戦士'], [0x2, '魔法使い'], [0x4, '天使'], [0x8, '悪魔'], [0x10, 'アンデット'], [0x20, '機械'], [0x40, '水'],
  [0x80, '炎'], [0x100, '岩石'], [0x200, '鳥獣'], [0x400, '植物'], [0x800, '昆虫'], [0x1000, '雷'], [0x2000, 'ドラゴン'], [0x4000, '獣'],
  [0x8000, '獣戦士'], [0x10000, '恐竜'], [0x20000, '魚'], [0x40000, '海竜'], [0x80000, '爬虫類'], [0x100000, 'サイキック'],
  [0x200000, '幻神獣'], [0x400000, '創造神']];
export const SEARCH_PAGE = 60;

export function isExtraDeckCard(card) {
  return card?.type != null && (card.type & EXTRA_MASK) !== 0;
}

/**
 * 検索の欄の値 → SearchCards の param（数の欄は整数だけ。空・読めない欄は送らない）。
 * lflist（card_id → 0 / 1 / 2 の Map）があれば、禁止・制限の絞り込みを番号の一覧にする
 */
export function buildSearchParam(v, offset = 0, lflist = null, removedIds = null) {
  const p = { language_code: v.language_code || 'jp', limit: SEARCH_PAGE };
  if (offset > 0) p.offset = offset;
  for (const k of ['query', 'text']) {
    const s = String(v[k] ?? '').trim();
    if (s) p[k] = s;
  }
  Object.assign(p, KINDS.find(([k]) => k === (v.kind ?? ''))?.[2] ?? {});
  const sub = (SUBKINDS[p.kind] ?? []).find(([k]) => k === v.subkind);
  if (sub) Object.assign(p, sub[2]);
  const limit = LIMITS.find(([k]) => k === (v.limit ?? ''))?.[2];
  if (limit && lflist) p.ids = [...lflist.entries()].filter(([, n]) => limit.includes(n)).map(([id]) => Number(id));
  if (v.removed && removedIds) {   // デッキから抜いたカード（禁止・制限と両方なら重なるものだけ）
    const ids = removedIds.map(Number);
    p.ids = p.ids ? p.ids.filter((id) => ids.includes(id)) : ids;
  }
  if (v.hasImage) p.has_image = true;
  const monster = p.kind !== 'spell' && p.kind !== 'trap';   // 魔法・罠ではモンスターの欄を送らない
  for (const k of monster ? ['attribute', 'race'] : []) {
    if (Number(v[k])) p[k] = Number(v[k]);
  }
  for (const k of monster ? ['level_min', 'level_max', 'atk_min', 'atk_max', 'def_min', 'def_max'] : []) {
    const s = String(v[k] ?? '').trim();
    if (/^-?\d+$/.test(s)) p[k] = Number(s);
  }
  return p;
}

/** 条件が何も無い（名前も絞り込みも空）なら検索しない */
export function hasSearchCondition(v) {
  const p = buildSearchParam(v);
  // 画像があるカードだけ・禁止 / 制限・抜いたカードだけでも探す（データベースのカードは 2011 年 3 月までなので年で絞る欄は無い）
  return Object.keys(p).some((k) => !['language_code', 'limit'].includes(k)) || Boolean(v.limit) || Boolean(v.removed);
}

const bits = (value, table, suffix = '') => table.filter(([b]) => (Number(value) & b) !== 0).map(([, n]) => n + suffix).join('・');

/** 一行の説明: モンスターは「★4 光 戦士族 1800/1600」、魔法・罠は「魔法」「罠」 */
export function cardLine(card) {
  const t = Number(card?.type ?? 0);
  if (t & TYPE_SPELL) return '魔法';
  if (t & TYPE_TRAP) return '罠';
  if (!(t & TYPE_MONSTER)) return '';
  const num = (x) => (x == null ? '' : Number(x) < 0 ? '?' : String(x));
  return `★${Number(card.level ?? 0) & 255} ${bits(card.attribute, ATTRIBUTES)} ${bits(card.race, RACES, '族')} ${num(card.atk)}/${num(card.def)}`.trim();
}

// ---- デッキの整列（モンスター → 魔法 → 罠 → エクストラ。前の DeckManagement と同じ順） ----
const T_NORMAL = 0x10;
const T_EFFECT = 0x20;
const T_RITUAL = 0x80;
const T_QUICKPLAY = 0x10000;
const T_CONTINUOUS = 0x20000;
const T_EQUIPMENT = 0x40000;
const T_FIELD = 0x80000;
const T_COUNTER = 0x100000;
const T_FUSION = 0x40;

const monsterOrder = (t) => ((t & T_NORMAL) ? 0 : (t & T_RITUAL) ? 2 : (t & T_EFFECT) ? 1 : 3);
const spellOrder = (t) => ((t & T_QUICKPLAY) ? 1 : (t & T_CONTINUOUS) ? 2 : (t & T_EQUIPMENT) ? 3 : (t & T_FIELD) ? 4 : (t & T_RITUAL) ? 5 : 0);
const trapOrder = (t) => ((t & T_CONTINUOUS) ? 1 : (t & T_COUNTER) ? 2 : 0);
const byReading = (a, b) => String(a.card_reading ?? a.card_name ?? '').localeCompare(String(b.card_reading ?? b.card_name ?? ''), 'ja')
  || String(a.card_id).localeCompare(String(b.card_id));

export function sortDeckCards(cards) {
  const main = cards.filter((c) => !isExtraDeckCard(c));
  const extra = cards.filter((c) => isExtraDeckCard(c));
  const t = (c) => Number(c.type ?? 0);
  const monsters = main.filter((c) => t(c) & TYPE_MONSTER).sort((a, b) => monsterOrder(t(a)) - monsterOrder(t(b))
    || (Number(b.level ?? 0) & 255) - (Number(a.level ?? 0) & 255) || byReading(a, b));
  const spells = main.filter((c) => t(c) & TYPE_SPELL).sort((a, b) => spellOrder(t(a)) - spellOrder(t(b)) || byReading(a, b));
  const traps = main.filter((c) => t(c) & TYPE_TRAP).sort((a, b) => trapOrder(t(a)) - trapOrder(t(b)) || byReading(a, b));
  const others = main.filter((c) => !(t(c) & (TYPE_MONSTER | TYPE_SPELL | TYPE_TRAP)));
  const extraSorted = extra.slice().sort((a, b) => Number(!(t(a) & T_FUSION)) - Number(!(t(b) & T_FUSION))
    || (Number(b.level ?? 0) & 255) - (Number(a.level ?? 0) & 255) || byReading(a, b));
  return [...monsters, ...spells, ...traps, ...others, ...extraSorted];
}
